import { createSimpleStore } from 'react-simple-reducer'
import { QuestaoModel } from '../models/QuestaoModel'
import api, { ICounter, IQueryParams } from '../api'
import { ModuloModel } from '../models/ModuloModel'
import { toast } from 'react-toastify'
import { Util } from '../components/Util'

export interface IQuestoesState {
  local?: string
  moduloAtualId?: number
  modulos?: ModuloModel[]
  questoes?: QuestaoModel[]
  totalQuestoes?: number
  isEditingQuestao?: boolean
  selectedQuestao?: QuestaoModel
  query?: IQueryParams
}

export const initialState: IQuestoesState = {
  modulos: [],
  questoes: [],
  query: { limit: 10, offset: 0 },
  totalQuestoes: 0,
}

const questoesStore = createSimpleStore(
  initialState,
  {
    addQuestaoSuccess(state, payload: QuestaoModel) {
      const questoes = state.questoes.find((x) => x.id === payload.id)
        ? state.questoes.map((x) => (x.id === payload.id ? payload : x))
        : [...state.questoes, payload]

      state.totalQuestoes =
        state.totalQuestoes + Math.max(questoes.length - state.questoes.length, 0)
      state.questoes = questoes
      state.isEditingQuestao = false
      state.selectedQuestao = { questoesAlternativas: [] }
    },
    setLocal(state, payload: string) {
      state.local = payload
    },
    setModuloAtualId(state, payload: number) {
      state.moduloAtualId = payload
    },
    selectQuestao(state, payload: QuestaoModel) {
      state.selectedQuestao = payload
      state.isEditingQuestao = true
    },
    setQueryParams(state, payload: IQueryParams) {
      state.query = { ...state.query, ...payload }
    },
    addQuestao(state) {
      state.selectedQuestao = { questoesAlternativas: [] }
      state.isEditingQuestao = true
    },
    updateQuestaoStarted(state) {
      // state is already being handled by loading state if needed
    },
    updateQuestaoSuccess(state) {
      state.isEditingQuestao = false
    },
    updateQuestaoError(state, payload: string) {
      // Error handling
    },
    clearQuestao(state) {
      state.selectedQuestao = { questoesAlternativas: [] }
      state.isEditingQuestao = false
    },
    questoesFetchStarted(state) {
      // state is already being handled
    },
    questoesFetchSuccess(state, payload: ICounter<QuestaoModel>) {
      state.questoes = payload.rows
      state.totalQuestoes = payload.count
    },
    questoesFetchError(state, payload: string) {
      // Error handling
    },
    modulosFetchStarted(state) {
      // state is already being handled
    },
    modulosFetchSuccess(state, payload: ModuloModel[]) {
      state.modulos = payload
    },
    modulosFetchError(state, payload: string) {
      // Error handling
    },
    reviewQuestaoStarted(state) {
      // state is already being handled
    },
    reviewQuestaoSuccess(state) {
      // state is already being handled
    },
    reviewQuestaoError(state, payload: string) {
      // Error handling
    },
    deleteQuestaoStarted(state) {
      // state is already being handled
    },
    deleteQuestaoSuccess(state, payload: string) {
      state.questoes = state.questoes.filter((x) => '' + x.id !== payload)
      state.totalQuestoes = state.totalQuestoes - 1
    },
    deleteQuestaoError(state, payload: string) {
      // Error handling
    },
  },
  {
    thunks: {
      fetchQuestoes:
        ({ query, local, moduloAtualId }) =>
        async (dispatch) => {
          dispatch(questoesStore.actions.questoesFetchStarted())
          try {
            const questoes = await api.Questoes.get(query, moduloAtualId, local)
            dispatch(questoesStore.actions.questoesFetchSuccess(questoes))
          } catch (error) {
            dispatch(questoesStore.actions.questoesFetchError(error.message))
          }
        },
      fetchModulos: () => async (dispatch) => {
        dispatch(questoesStore.actions.modulosFetchStarted())
        try {
          const modulosResponse = await api.Modulos.get({})
          dispatch(questoesStore.actions.modulosFetchSuccess(modulosResponse.rows))
        } catch (error) {
          dispatch(questoesStore.actions.modulosFetchError(error.message))
        }
      },
      reviewQuestao: (questao: QuestaoModel) => async (dispatch) => {
        dispatch(questoesStore.actions.reviewQuestaoStarted())
        try {
          await api.Questoes.createOrUpdate({ ...questao, revisada: true })
          dispatch(questoesStore.actions.reviewQuestaoSuccess())
          toast('Questão revisada com sucesso')
        } catch (error) {
          dispatch(questoesStore.actions.reviewQuestaoError(error.message))
          toast(error.message || 'Erro ao revisar questão')
        }
      },
      updateQuestao: (questao: QuestaoModel) => async (dispatch) => {
        dispatch(questoesStore.actions.updateQuestaoStarted())
        try {
          await api.Questoes.createOrUpdate({ ...questao })
          dispatch(questoesStore.actions.updateQuestaoSuccess())
          toast('Questão atualizada com sucesso')
        } catch (error) {
          dispatch(questoesStore.actions.updateQuestaoError(error.message))
          toast(error.message || 'Erro ao atualizar questão')
        }
      },
      deleteQuestao: (questaoId: string) => async (dispatch) => {
        if (!(await Util.confirm('Tem certeza que deseja deletar esta questão'))) return
        dispatch(questoesStore.actions.deleteQuestaoStarted())
        try {
          await api.Questoes.delete(questaoId)
          dispatch(questoesStore.actions.deleteQuestaoSuccess(questaoId))
          toast('Questão deletada com sucesso')
        } catch (error) {
          dispatch(questoesStore.actions.deleteQuestaoError(error.message))
          toast(error.message || 'Erro ao deletar questão')
        }
      },
    },
  }
)

export { questoesStore }
export const {
  Provider: QuestoesProvider,
  useDispatch: useQuestoesDispatch,
  useState: useQuestoesState,
  useSelector: useQuestoesSelector,
} = questoesStore
